<template>
  <div class="video-handler p-4 max-w-2xl mx-auto">
    <h2 class="text-xl font-semibold mb-4">1. Upload Video</h2>

    <!-- Video Upload Section -->
    <div class="upload-section mb-6 p-4 border rounded-lg shadow-sm bg-gray-50">
      <label for="video-upload" class="block text-sm font-medium text-gray-700 mb-2">
        Select Video File:
      </label>
      <input
        type="file"
        id="video-upload"
        ref="videoFileInput"
        accept="video/*"
        @change="handleFileSelect"
        class="block w-full text-sm text-gray-500
               file:mr-4 file:py-2 file:px-4
               file:rounded-full file:border-0
               file:text-sm file:font-semibold
               file:bg-blue-50 file:text-blue-700
               hover:file:bg-blue-100"
      />
      <div v-if="uploadProgress !== null" class="progress-indicator mt-4">
        <p class="text-sm text-gray-600">Uploading: {{ uploadProgress }}%</p>
        <progress
            class="w-full h-2 [&::-webkit-progress-bar]:rounded-lg [&::-webkit-progress-value]:rounded-lg   [&::-webkit-progress-bar]:bg-slate-300 [&::-webkit-progress-value]:bg-blue-600 [&::-moz-progress-bar]:bg-blue-600"
            :value="uploadProgress"
            max="100"
        >
        </progress>
      </div>
      <p v-if="uploadError" class="text-red-500 text-sm mt-2">{{ uploadError }}</p>
    </div>

    <h2 class="text-xl font-semibold mb-4 mt-8">2. Define Region of Interest</h2>

    <!-- Video Player and Drawing Section -->
    <div v-if="videoUrl" class="player-section relative mt-6 border rounded-lg shadow-sm bg-gray-50 overflow-hidden">
      <!-- Instructions -->
      <p class="p-4 text-sm text-gray-600 bg-blue-50 border-b">
        Click and drag on the video below to select the area you want to monitor for changes.
      </p>

      <div class="relative w-full p-4" ref="playerContainer">
        <video
          ref="videoPlayer"
          controls
          :src="videoUrl"
          class="w-full rounded block"
          @loadedmetadata="onVideoMetadataLoaded"
          @mousedown.prevent="startDrawing"
          @mousemove.prevent="draw"
          @mouseup.prevent="stopDrawing"
          @mouseleave.prevent="stopDrawing"
          crossorigin="anonymous"
        >
          Your browser does not support the video tag.
        </video>

        <!-- Drawing Rectangle Overlay -->
        <div
          v-if="isDrawing || roiRect.width > 0"
          class="absolute border-2 border-red-500 bg-red-500 bg-opacity-25 pointer-events-none"
          :style="roiStyle"
        ></div>
      </div>

      <!-- Display ROI Coordinates -->
      <div v-if="normalizedRoi.width > 0" class="p-4 border-t bg-gray-100 text-xs text-gray-700">
        <p class="font-medium mb-1">Selected Region (Normalized Coordinates):</p>
        <pre>x: {{ normalizedRoi.x.toFixed(4) }}
y: {{ normalizedRoi.y.toFixed(4) }}
w: {{ normalizedRoi.width.toFixed(4) }}
h: {{ normalizedRoi.height.toFixed(4) }}</pre>
        <button 
          @click="resetRoi"
          class="mt-2 px-3 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded"
          >
          Clear Selection
        </button>
      </div>

    </div>
     <div v-else class="player-section mt-6 p-8 border rounded-lg shadow-sm text-center text-gray-500 bg-gray-50">
        <p>Video player and drawing area will appear here after upload.</p>
     </div>

     <!-- Placeholder for Step 3 -->
     <h2 class="text-xl font-semibold mb-4 mt-8">3. Analyze (Coming Soon)</h2>
     <div class="p-4 border rounded-lg shadow-sm text-center text-gray-500 bg-gray-50">
         <p>Analysis controls will appear here.</p>
         <button 
            v-if="normalizedRoi.width > 0" 
            class="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full disabled:opacity-50"
            disabled 
            title="Analysis functionality not yet implemented"
         >
            Find Changes
         </button>
     </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const videoFileInput = ref(null);
const uploadProgress = ref(null);
const videoUrl = ref(null);
const uploadError = ref(null);

const playerContainer = ref(null);
const videoPlayer = ref(null);
const videoDimensions = ref({ width: 0, height: 0 });

const isDrawing = ref(false);
const startCoords = ref({ x: 0, y: 0 });
const roiRect = ref({ x: 0, y: 0, width: 0, height: 0 });
const normalizedRoi = ref({ x: 0, y: 0, width: 0, height: 0 });

const roiStyle = computed(() => {
  const videoElement = videoPlayer.value;
  if (!videoElement) return {};

  const offsetX = videoElement.offsetLeft || 0;
  const offsetY = videoElement.offsetTop || 0;

  return {
    left: `${offsetX + roiRect.value.x}px`,
    top: `${offsetY + roiRect.value.y}px`,
    width: `${roiRect.value.width}px`,
    height: `${roiRect.value.height}px`,
  };
});

const handleFileSelect = async (event) => {
  const file = event.target.files[0];
  if (!file) {
    return;
  }

  uploadProgress.value = 0;
  uploadError.value = null;
  videoUrl.value = null;
  resetRoi();

  const formData = new FormData();
  formData.append('video', file);

  try {
    console.log('Starting upload to http://localhost:5000/api/upload');
    const response = await fetch('http://localhost:5000/api/upload', {
      method: 'POST',
      body: formData,
    });

    console.log('Response status:', response.status);
    
    const responseText = await response.text();
    
    if (!response.ok) {
      let errorMessage = 'Server error with status: ' + response.status;
      try {
        const errorData = JSON.parse(responseText);
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        console.error('Could not parse error response as JSON', parseError);
        errorMessage += ' - Response was not valid JSON: ' + responseText.substring(0, 100) + '...';
      }
      throw new Error(errorMessage);
    }

    const result = JSON.parse(responseText);
    console.log('Upload successful:', result);
    videoUrl.value = `http://localhost:5000${result.videoPath}`; 
    uploadProgress.value = null;

  } catch (error) {
    console.error('Upload failed:', error);
    uploadError.value = `Upload failed: ${error.message}`;
    uploadProgress.value = null;
  }
};

const onVideoMetadataLoaded = () => {
  if (videoPlayer.value) {
    videoDimensions.value = {
      width: videoPlayer.value.videoWidth,
      height: videoPlayer.value.videoHeight
    };
    console.log('Video metadata loaded:', videoDimensions.value);
  }
};

const getRelativeCoords = (event) => {
  if (!videoPlayer.value) return { x: 0, y: 0 };
  const rect = videoPlayer.value.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top
  };
};

const startDrawing = (event) => {
  if (!videoPlayer.value) return;
  resetRoi();
  isDrawing.value = true;
  const { x, y } = getRelativeCoords(event);
  startCoords.value = { x, y };
  roiRect.value = { x, y, width: 0, height: 0 };
  console.log('Start drawing at:', startCoords.value);
};

const draw = (event) => {
  if (!isDrawing.value || !videoPlayer.value) return;

  const { x: currentX, y: currentY } = getRelativeCoords(event);
  const videoRect = videoPlayer.value.getBoundingClientRect();

  const constrainedX = Math.max(0, Math.min(currentX, videoRect.width));
  const constrainedY = Math.max(0, Math.min(currentY, videoRect.height));

  const width = Math.abs(constrainedX - startCoords.value.x);
  const height = Math.abs(constrainedY - startCoords.value.y);
  
  const rectX = Math.min(startCoords.value.x, constrainedX);
  const rectY = Math.min(startCoords.value.y, constrainedY);

  roiRect.value = { x: rectX, y: rectY, width, height };
};

const stopDrawing = () => {
  if (!isDrawing.value) return;
  isDrawing.value = false;

  if (videoPlayer.value && roiRect.value.width > 5 && roiRect.value.height > 5) {
    const videoRect = videoPlayer.value.getBoundingClientRect();
    normalizedRoi.value = {
      x: roiRect.value.x / videoRect.width,
      y: roiRect.value.y / videoRect.height,
      width: roiRect.value.width / videoRect.width,
      height: roiRect.value.height / videoRect.height
    };
    console.log('ROI selection finished (normalized):', normalizedRoi.value);
  } else {
    console.log('ROI too small or invalid, resetting.');
    resetRoi();
  }
};

const resetRoi = () => {
    roiRect.value = { x: 0, y: 0, width: 0, height: 0 };
    normalizedRoi.value = { x: 0, y: 0, width: 0, height: 0 };
    isDrawing.value = false;
    console.log('ROI reset');
};

</script>

<style scoped>
.player-section video {
  cursor: crosshair;
  display: block;
}
</style> 